import type { QueryClient } from '@tanstack/react-query';

interface HouseholdEdits {
  tails: Map<string, Promise<void>>;
  pending: number;
  refreshPeriods: Set<string>;
  refreshHousehold: boolean;
}

const clients = new WeakMap<QueryClient, Map<string, HouseholdEdits>>();

/**
 * Acquire before reading or changing the optimistic cache, release in onSettled.
 * Mutation scope alone only queues the request: its onMutate would still take a
 * snapshot of another edit's preview and could later restore an uncommitted value.
 */
export async function acquireLineEdit(
  client: QueryClient,
  householdId: string,
  period: string,
  lineId: string,
) {
  let households = clients.get(client);
  if (!households) {
    households = new Map();
    clients.set(client, households);
  }
  let edits = households.get(householdId);
  if (!edits) {
    edits = {
      tails: new Map(),
      pending: 0,
      refreshPeriods: new Set(),
      refreshHousehold: false,
    };
    households.set(householdId, edits);
  }
  const rowKey = JSON.stringify([period, lineId]);
  const previous = edits.tails.get(rowKey);
  let release!: () => void;
  const tail = new Promise<void>((resolve) => {
    release = resolve;
  });
  edits.tails.set(rowKey, tail);
  edits.pending += 1;
  await previous;

  let finished = false;
  return (refresh?: 'month' | 'household') => {
    if (finished) return;
    finished = true;
    if (refresh === 'household') edits.refreshHousehold = true;
    if (refresh === 'month') edits.refreshPeriods.add(period);
    if (edits.tails.get(rowKey) === tail) edits.tails.delete(rowKey);
    edits.pending -= 1;
    release();

    // A successful row must not refetch over another row's pending preview.
    // Future-scope writes refresh the household, so wait across its periods too.
    if (edits.pending !== 0) return;
    households.delete(householdId);
    if (edits.refreshHousehold) {
      void client.invalidateQueries({ queryKey: ['h', householdId] });
    } else {
      for (const dirtyPeriod of edits.refreshPeriods) {
        void client.invalidateQueries({ queryKey: ['h', householdId, 'months', dirtyPeriod] });
      }
    }
  };
}
